import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, Link, useLocation } from "react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  teacherSignupSchema,
  type TeacherSignupFormData,
} from "../../schemas/auth.schema";
import { getApiClient } from "../../api";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import Icon from "../../components/AppIcon";
import TurnstileWidget, { type TurnstileWidgetHandle } from "../../components/auth/TurnstileWidget";
import { useEmailSpellCheck } from "../../hooks/useEmailSpellCheck";
import { getApiErrorMessage } from "../../utils/apiError";

const formatIndianPhoneNumber = (phoneNumber: string): string => {
  const digits = phoneNumber.replace(/\D/g, "");

  if (!digits) return "";

  if (digits.length === 10) {
    return `+91${digits}`;
  }

  if (digits.length === 11 && digits.startsWith("0")) {
    return `+91${digits.slice(1)}`;
  }

  if (digits.length === 12 && digits.startsWith("91")) {
    return `+${digits}`;
  }

  return phoneNumber.trim().startsWith("+") ? phoneNumber.trim() : `+91${digits}`;
};

const TeacherSignup: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTimerRef = useRef<number | null>(null);
  const turnstileWidgetRef = useRef<TurnstileWidgetHandle | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<string>("");
  const [isValidatingCode, setIsValidatingCode] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string>("");
  const [isSendingPhoneOtp, setIsSendingPhoneOtp] = useState(false);
  const [isVerifyingPhoneOtp, setIsVerifyingPhoneOtp] = useState(false);
  const [phoneOtpRequested, setPhoneOtpRequested] = useState(false);
  const [verifiedPhoneOtp, setVerifiedPhoneOtp] = useState<{ phone: string; otp: string } | null>(
    null
  );
  const [phoneOtpFeedback, setPhoneOtpFeedback] = useState<{
    tone: "info" | "success" | "error";
    message: string;
  } | null>(null);
  const [codeValidation, setCodeValidation] = useState<{
    valid: boolean;
    message: string;
  } | null>(null);

  const { suggestion, checkEmail, applySuggestion } = useEmailSpellCheck();

  const {
    register,
    handleSubmit: handleFormSubmit,
    formState: { errors, isSubmitting },
    watch,
    getValues,
    setValue,
  } = useForm<TeacherSignupFormData>({
    resolver: zodResolver(teacherSignupSchema),
    mode: "onBlur",
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      username: "",
      password: "",
      confirmPassword: "",
      phone: "",
      gender: undefined,
      invitationCode: "",
      phoneOtp: "",
    },
  });

  const invitationCode = watch("invitationCode");
  const phoneNumber = watch("phone");
  const phoneOtp = watch("phoneOtp");
  const password = watch("password");
  const confirmPassword = watch("confirmPassword");
  const normalizedPhoneNumber = phoneNumber?.trim() || "";
  const apiPhoneNumber = formatIndianPhoneNumber(normalizedPhoneNumber);
  const normalizedPhoneOtp = phoneOtp?.trim() || "";
  const normalizedPassword = password?.trim() || "";
  const normalizedConfirmPassword = confirmPassword?.trim() || "";
  const hasPasswordMatchHint = normalizedPassword.length > 0 && normalizedConfirmPassword.length > 0;
  const isPasswordMatch = hasPasswordMatchHint && normalizedPassword === normalizedConfirmPassword;
  const isPhoneOtpVerified =
    !!verifiedPhoneOtp &&
    verifiedPhoneOtp.phone === apiPhoneNumber &&
    verifiedPhoneOtp.otp === normalizedPhoneOtp;

  const email = watch("email");

  const validateInvitationCode = useCallback(async (codeToValidate = invitationCode, currentEmail = email) => {
    if (!codeToValidate || codeToValidate.length < 6) {
      setCodeValidation(null);
      return;
    }

    setIsValidatingCode(true);
    setCodeValidation(null);

    try {
      const apiClient = getApiClient();
      const response = await apiClient.getHttpClient().post("/auth/validate-invitation", {
        invitationCode: codeToValidate,
        email: currentEmail?.trim() || undefined,
      });

      const invitationEmail =
        typeof (response as any).email === 'string' ? (response as any).email.trim() : '';

      if (invitationEmail && !getValues('email')?.trim()) {
        setValue('email', invitationEmail, { shouldValidate: true, shouldDirty: false });
      }

      setCodeValidation({
        valid: (response as any).valid,
        message: (response as any).message,
      });
    } catch (error: any) {
      setCodeValidation({
        valid: false,
        message: getApiErrorMessage(error, "Failed to validate invitation code"),
      });
    } finally {
      setIsValidatingCode(false);
    }
  }, [getValues, invitationCode, email, setValue]);

  const sendPhoneOtp = useCallback(async () => {
    const phone = apiPhoneNumber;
    if (!phone) {
      setPhoneOtpFeedback({
        tone: "error",
        message: "Enter a 10-digit phone number first.",
      });
      return;
    }

    setError("");
    setPhoneOtpFeedback(null);
    setIsSendingPhoneOtp(true);

    try {
      const apiClient = getApiClient();
      const response = await apiClient.getHttpClient().request({
        method: "POST",
        url: "/auth/teacher-signup/send-phone-otp",
        data: {
          phoneNumber: phone,
          invitationCode: getValues("invitationCode"),
        },
      });

      setPhoneOtpRequested(true);
      setVerifiedPhoneOtp(null);
      setValue("phoneOtp", "", { shouldValidate: true, shouldDirty: true });
      setPhoneOtpFeedback({
        tone: "success",
        message: (response as any)?.message || "OTP sent successfully.",
      });
    } catch (error: any) {
      setPhoneOtpRequested(false);
      setVerifiedPhoneOtp(null);
      setPhoneOtpFeedback({
        tone: "error",
        message: getApiErrorMessage(error, "Failed to send OTP."),
      });
    } finally {
      setIsSendingPhoneOtp(false);
    }
  }, [apiPhoneNumber, getValues, setValue]);

  const verifyPhoneOtp = useCallback(async () => {
    const phone = apiPhoneNumber;
    const otp = normalizedPhoneOtp;

    if (!phone) {
      setPhoneOtpFeedback({
        tone: "error",
        message: "Enter a 10-digit phone number first.",
      });
      return;
    }

    if (!otp) {
      setPhoneOtpFeedback({
        tone: "error",
        message: "Enter the OTP sent to your phone.",
      });
      return;
    }

    setError("");
    setPhoneOtpFeedback(null);
    setIsVerifyingPhoneOtp(true);

    try {
      const apiClient = getApiClient();
      const response = await apiClient.getHttpClient().request({
        method: "POST",
        url: "/auth/validate-phone-otp",
        data: {
          phoneNumber: phone,
          phoneOtp: otp,
        },
      });

      if ((response as any)?.valid) {
        setVerifiedPhoneOtp({ phone, otp });
      } else {
        setVerifiedPhoneOtp(null);
      }

      setPhoneOtpFeedback({
        tone: (response as any)?.valid ? "success" : "error",
        message: (response as any)?.message || "Phone OTP validation failed.",
      });
    } catch (error: any) {
      setVerifiedPhoneOtp(null);
      setPhoneOtpFeedback({
        tone: "error",
        message: getApiErrorMessage(error, "Failed to verify OTP."),
      });
    } finally {
      setIsVerifyingPhoneOtp(false);
    }
  }, [apiPhoneNumber, normalizedPhoneOtp]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const codeFromUrl = params.get('code')?.trim().toUpperCase();

    if (!codeFromUrl) return;

    setValue('invitationCode', codeFromUrl, { shouldValidate: true, shouldDirty: true });
    void validateInvitationCode(codeFromUrl);
  }, [location.search, setValue, validateInvitationCode]);

  useEffect(() => {
    setPhoneOtpRequested(false);
    setVerifiedPhoneOtp(null);
    setPhoneOtpFeedback(null);
    setValue("phoneOtp", "", { shouldValidate: true, shouldDirty: false });
  }, [normalizedPhoneNumber, setValue]);

  useEffect(() => {
    if (phoneOtpRequested && !normalizedPhoneNumber) {
      setPhoneOtpRequested(false);
    }
  }, [normalizedPhoneNumber, phoneOtpRequested]);

  const handleSubmit = async (data: TeacherSignupFormData) => {
    setError("");
    setSuccess("");

    if (import.meta.env.PROD && !turnstileToken) {
      setError("Please complete the verification challenge before signing up.");
      return;
    }

    if (normalizedPhoneNumber && !isPhoneOtpVerified) {
      setError("Please verify your phone number before signing up.");
      return;
    }

    try {
      const apiClient = getApiClient();
      const response = await apiClient.getHttpClient().request({
        method: 'POST',
        url: '/auth/sign-up/teacher',
        headers: turnstileToken ? { 'X-Turnstile-Token': turnstileToken } : undefined,
        data: {
          email: data.email,
          username: data.username,
          password: data.password,
          firstName: data.firstName,
          lastName: data.lastName,
          phoneNumber: data.phone ? formatIndianPhoneNumber(data.phone) : undefined,
          phoneOtp: data.phoneOtp || undefined,
          gender: data.gender || undefined,
          invitationCode: data.invitationCode,
        },
      });

      setTurnstileToken("");
      turnstileWidgetRef.current?.reset();
      setVerifiedPhoneOtp(null);
      setPhoneOtpFeedback(null);

      setSuccess(response?.message || "Account created successfully!");
      
      // Redirect to login after 2 seconds
      if (redirectTimerRef.current) {
        window.clearTimeout(redirectTimerRef.current);
      }
      redirectTimerRef.current = window.setTimeout(() => {
        navigate("/auth/login", {
          // Share-link signups must verify their email first; the backend says so.
          state: { message: response?.message || "Account created! Please sign in with your credentials." },
        });
      }, 2000);
    } catch (error: any) {
      console.error("Signup failed:", error);

      // Turnstile tokens are single-use and the backend has already spent this
      // one, so a retry needs a fresh challenge.
      turnstileWidgetRef.current?.reset();

      let errorMessage = "Signup failed. Please try again.";
      
      if (error.response?.status === 400) {
        errorMessage = getApiErrorMessage(error, "Invalid signup data");
      } else if (error.response?.status === 409) {
        errorMessage = "Email or username already exists";
      } else if (error.response?.status === 500) {
        errorMessage = "Server error. Please try again later.";
      } else if (error.message?.includes("Network")) {
        errorMessage = "Network error. Please check your internet connection.";
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      setError(errorMessage);
    }
  };

  useEffect(() => {
    return () => {
      if (redirectTimerRef.current) {
        window.clearTimeout(redirectTimerRef.current);
      }
    };
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-linear-to-br from-indigo-50 via-white to-cyan-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl w-full space-y-8 bg-white p-8 rounded-xl shadow-lg border border-gray-100">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
            <Icon name="UserPlus" size={24} className="text-primary" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 tracking-tight">
            Teacher Registration
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Create your teacher account with an invitation code
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleFormSubmit(handleSubmit)}>
          {/* Invitation Code */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <label
              htmlFor="invitationCode"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Invitation Code <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-2">
              <div className="flex-1">
                <Input
                  id="invitationCode"
                  type="text"
                  placeholder="Enter your invitation code"
                  disabled={isSubmitting}
                  {...register("invitationCode")}
                  onBlur={() => {
                    void validateInvitationCode();
                  }}
                  className={errors.invitationCode ? "border-red-500 focus:ring-red-500" : ""}
                />
              </div>
              <Button
                type="button"
                onClick={() => {
                  void validateInvitationCode();
                }}
                disabled={isValidatingCode || !invitationCode}
                variant="outline"
                className="px-4"
              >
                {isValidatingCode ? (
                  <Icon name="Loader2" size={18} className="animate-spin" />
                ) : (
                  "Verify"
                )}
              </Button>
            </div>
            {errors.invitationCode && (
              <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                <Icon name="AlertCircle" size={14} />
                {errors.invitationCode.message}
              </p>
            )}
            {codeValidation && (
              <p
                className={`mt-1 text-sm flex items-center gap-1 ${
                  codeValidation.valid ? "text-green-600" : "text-red-600"
                }`}
              >
                <Icon
                  name={codeValidation.valid ? "CheckCircle" : "XCircle"}
                  size={14}
                />
                {codeValidation.message}
              </p>
            )}
          </div>

          {/* Personal Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="firstName"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                First Name <span className="text-red-500">*</span>
              </label>
              <Input
                id="firstName"
                type="text"
                placeholder="Enter your first name"
                disabled={isSubmitting}
                {...register("firstName")}
                className={errors.firstName ? "border-red-500 focus:ring-red-500" : ""}
              />
              {errors.firstName && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <Icon name="AlertCircle" size={14} />
                  {errors.firstName.message}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="lastName"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Last Name <span className="text-red-500">*</span>
              </label>
              <Input
                id="lastName"
                type="text"
                placeholder="Enter your last name"
                disabled={isSubmitting}
                {...register("lastName")}
                className={errors.lastName ? "border-red-500 focus:ring-red-500" : ""}
              />
              {errors.lastName && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <Icon name="AlertCircle" size={14} />
                  {errors.lastName.message}
                </p>
              )}
            </div>
          </div>

          {/* Account Information */}
          <div className="space-y-4">
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Email Address <span className="text-red-500">*</span>
              </label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="Enter your email"
                disabled={isSubmitting}
                {...register("email")}
                onBlur={(e) => {
                  register("email").onBlur(e);
                  void validateInvitationCode();
                }}
                onChange={(e) => {
                  register("email").onChange(e);
                  checkEmail(e.target.value);
                }}
                suggestion={suggestion?.full}
                onSuggestionClick={() => {
                  const newVal = applySuggestion();
                  if (newVal) {
                    setValue("email", newVal, { shouldValidate: true });
                  }
                }}
                className={errors.email ? "border-red-500 focus:ring-red-500" : ""}
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <Icon name="AlertCircle" size={14} />
                  {errors.email.message}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="username"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Username <span className="text-red-500">*</span>
              </label>
              <Input
                id="username"
                type="text"
                autoComplete="username"
                placeholder="Choose a username"
                disabled={isSubmitting}
                {...register("username")}
                className={errors.username ? "border-red-500 focus:ring-red-500" : ""}
              />
              {errors.username && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <Icon name="AlertCircle" size={14} />
                  {errors.username.message}
                </p>
              )}
            </div>
          </div>

          {/* Password */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Password <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  placeholder="Create a password"
                  disabled={isSubmitting}
                  {...register("password")}
                  className={errors.password ? "border-red-500 focus:ring-red-500" : ""}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isSubmitting}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 disabled:opacity-50"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  <Icon name={showPassword ? "EyeOff" : "Eye"} size={18} />
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <Icon name="AlertCircle" size={14} />
                  {errors.password.message}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Confirm Password <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  autoComplete="new-password"
                  placeholder="Confirm your password"
                  disabled={isSubmitting}
                  {...register("confirmPassword")}
                  className={[
                    errors.confirmPassword ? "border-red-500 focus:ring-red-500" : "",
                    hasPasswordMatchHint && isPasswordMatch
                      ? "border-green-500 focus:ring-green-500"
                      : "",
                    hasPasswordMatchHint && !isPasswordMatch
                      ? "border-amber-500 focus:ring-amber-500"
                      : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  disabled={isSubmitting}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 disabled:opacity-50"
                  aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                >
                  <Icon name={showConfirmPassword ? "EyeOff" : "Eye"} size={18} />
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <Icon name="AlertCircle" size={14} />
                  {errors.confirmPassword.message}
                </p>
              )}
              {!errors.confirmPassword && hasPasswordMatchHint && (
                <p
                  className={`mt-1 text-sm flex items-center gap-1 ${
                    isPasswordMatch ? "text-green-600" : "text-amber-600"
                  }`}
                >
                  <Icon
                    name={isPasswordMatch ? "CheckCircle" : "AlertCircle"}
                    size={14}
                  />
                  {isPasswordMatch
                    ? "Passwords match."
                    : "Passwords do not match yet."}
                </p>
              )}
            </div>
          </div>

          {/* Optional Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="phone"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Phone Number (Optional)
              </label>
              <div className="flex items-stretch overflow-hidden rounded-md border border-gray-300 focus-within:ring-2 focus-within:ring-primary focus-within:border-transparent">
                <span className="inline-flex items-center px-3 text-sm text-gray-600 bg-gray-50 border-r border-gray-200">
                  +91
                </span>
                <Input
                  id="phone"
                  type="tel"
                  inputMode="numeric"
                  placeholder="9876543210"
                  disabled={isSubmitting}
                  {...register("phone")}
                  className={`border-0 rounded-none focus:ring-0 ${errors.phone ? "text-red-700" : ""}`}
                />
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <Button
                  type="button"
                  variant="link"
                  size="xs"
                  className="h-auto px-0 text-primary"
                  onClick={() => {
                    void sendPhoneOtp();
                  }}
                  disabled={isSubmitting || isSendingPhoneOtp || !normalizedPhoneNumber}
                >
                  {isSendingPhoneOtp ? (
                    <span className="flex items-center gap-1">
                      <Icon name="Loader2" size={12} className="animate-spin" />
                      Sending OTP...
                    </span>
                  ) : (
                    "Send OTP"
                  )}
                </Button>
                {normalizedPhoneNumber && (
                  <span className="text-xs text-gray-500">
                    We&apos;ll send a verification code to +91 {normalizedPhoneNumber}.
                  </span>
                )}
              </div>
              {phoneOtpRequested && normalizedPhoneNumber && (
                <div className="mt-3 space-y-2">
                  <label
                    htmlFor="phoneOtp"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    OTP Code <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <Input
                        id="phoneOtp"
                        type="text"
                        inputMode="numeric"
                        placeholder="Enter the 6-digit OTP"
                        disabled={isSubmitting}
                        {...register("phoneOtp")}
                        className={errors.phoneOtp ? "border-red-500 focus:ring-red-500" : ""}
                      />
                    </div>
                    <Button
                      type="button"
                      onClick={() => {
                        void verifyPhoneOtp();
                      }}
                      disabled={isVerifyingPhoneOtp || !normalizedPhoneOtp || isSubmitting}
                      variant="outline"
                      className="px-4"
                    >
                      {isVerifyingPhoneOtp ? (
                        <Icon name="Loader2" size={18} className="animate-spin" />
                      ) : isPhoneOtpVerified ? (
                        "Verified"
                      ) : (
                        "Verify OTP"
                      )}
                    </Button>
                  </div>
                  {phoneOtpFeedback && (
                    <p
                      className={`text-sm flex items-center gap-1 ${
                        phoneOtpFeedback.tone === "success"
                          ? "text-green-600"
                          : phoneOtpFeedback.tone === "error"
                            ? "text-red-600"
                            : "text-gray-500"
                      }`}
                    >
                      <Icon
                        name={
                          phoneOtpFeedback.tone === "success"
                            ? "CheckCircle"
                            : phoneOtpFeedback.tone === "error"
                              ? "XCircle"
                              : "Info"
                        }
                        size={14}
                      />
                      {phoneOtpFeedback.message}
                    </p>
                  )}
                  {isPhoneOtpVerified && (
                    <p className="text-xs text-green-600 flex items-center gap-1">
                      <Icon name="CheckCircle" size={12} />
                      Phone number verified for this signup.
                    </p>
                  )}
                  {errors.phoneOtp && (
                    <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                      <Icon name="AlertCircle" size={14} />
                      {errors.phoneOtp.message}
                    </p>
                  )}
                </div>
              )}
              {errors.phone && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <Icon name="AlertCircle" size={14} />
                  {errors.phone.message}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="gender"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Gender (Optional)
              </label>
              <select
                id="gender"
                disabled={isSubmitting}
                {...register("gender")}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">Select gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
              {errors.gender && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <Icon name="AlertCircle" size={14} />
                  {errors.gender.message}
                </p>
              )}
            </div>
          </div>

          {/* Error/Success Messages */}
          {error && (
            <div className="rounded-md bg-red-50 p-4 border border-red-200">
              <div className="flex items-start">
                <Icon name="AlertCircle" size={20} className="text-red-600 mr-2 shrink-0 mt-0.5" />
                <div className="text-sm text-red-700">{error}</div>
              </div>
            </div>
          )}

          {success && (
            <div className="rounded-md bg-green-50 p-4 border border-green-200">
              <div className="flex items-start">
                <Icon name="CheckCircle" size={20} className="text-green-600 mr-2 shrink-0 mt-0.5" />
                <div className="text-sm text-green-700">{success}</div>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <div>
            <Button 
              type="submit" 
              disabled={
                isSubmitting ||
                (codeValidation !== null && !codeValidation.valid) ||
                (normalizedPhoneNumber.length > 0 && !isPhoneOtpVerified)
              } 
              className="w-full"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <Icon name="Loader2" size={18} className="animate-spin" />
                  Creating account...
                </span>
              ) : (
                "Create Teacher Account"
              )}
            </Button>
          </div>
        </form>

        <div className="mt-4">
          <TurnstileWidget
            ref={turnstileWidgetRef}
            onTokenChange={setTurnstileToken}
            action="teacher_signup"
          />
        </div>

        {/* Login Link */}
        <div className="text-center">
          <p className="text-sm text-gray-600">
            Already have an account?{" "}
            <Link
              to="/auth/login"
              className="font-medium text-primary hover:text-primary/80 transition-colors"
            >
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default TeacherSignup;
